import React, { useState, useEffect } from 'react';
import { useParams, Routes, Route, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, CircularProgress, Alert, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import * as api from '../../api';
import Header from '../../components/Header';

export default function RegionManage({ locale }) {
  const { regionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [region, setRegion] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewClub, setShowNewClub] = useState(false);
  const [newClub, setNewClub] = useState({ alias: '', title: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.getRegion(regionId).then(r => setRegion(r.data)),
      api.getClubs(regionId).then(r => setClubs(r.data || [])),
      api.getRegionAuth(regionId).then(r => setAuth(r.data)),
    ]).catch(e => setError(e.message || 'Failed to load')).finally(() => setLoading(false));
  }, [regionId]);

  const handleCreateClub = async () => {
    setSaving(true);
    try {
      await api.createClub({ ...newClub, region: regionId });
      const { data } = await api.getClubs(regionId);
      setClubs(data || []);
      setShowNewClub(false);
      setNewClub({ alias: '', title: '' });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!auth?.permissions?.includes('manage')) return <Container sx={{ mt: 4 }}><Alert severity="error">{t('noPermission')}</Alert></Container>;

  return (
    <>
      <Header locale={locale} />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>{t('manage')} - {region?.title || regionId}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('clubs')}</Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setShowNewClub(true)}>{t('add')}</Button>
        </Box>

        <List>
          {clubs.map(club => (
            <ListItem key={club.id} divider>
              <ListItemText primary={club.title} secondary={club.alias} />
              <ListItemSecondaryAction>
                <IconButton onClick={() => navigate(`/clubs/${club.id}/manage`)}><EditIcon /></IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        <Dialog open={showNewClub} onClose={() => setShowNewClub(false)}>
          <DialogTitle>{t('newClub')}</DialogTitle>
          <DialogContent>
            <TextField fullWidth label={t('alias')} value={newClub.alias} onChange={e => setNewClub(p => ({ ...p, alias: e.target.value }))} sx={{ mt: 1, mb: 2 }} />
            <TextField fullWidth label={t('title')} value={newClub.title} onChange={e => setNewClub(p => ({ ...p, title: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowNewClub(false)}>{t('cancel')}</Button>
            <Button variant="contained" onClick={handleCreateClub} disabled={saving || !newClub.alias || !newClub.title}>
              {saving ? <CircularProgress size={20} /> : t('save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
